// 批次 26（#21，Gitea issue #21）：config 其餘 7 頁中 display/picklist 特頁的 complexBody。
//
//   - JobTypeBody＝訓練類別設定（config-job-type，display 頁型，origin jobType.jsp 577 行）：
//     查詢（醫院單欄，:439-459，${organizationList} server render 無空 option＝預選第一筆）＋
//     查詢鈕（:454；權限包錯 config/jobTypeDepartment-ACCESS＋data-target 死 id＝legacy-known-issue
//     照實不鏡像＝D26-8）→ config/getJobTypeListAjax（:226）＋表 3 欄（:28-76：訓練類別名稱/
//     排序/狀態，舊欄全註解＝不鏡像）＋新增（:142-151）／名稱連結修改（showEdit :38-41）→
//     modal 9 欄（:472-577）＋check usage 刪除/啟停（:380-414）。
//   - JobTypeDepartmentBody＝訓練類別部門維護（config-job-type-department，crud-list 頁型，
//     origin jobTypeDepartment.jsp 512 行）：查詢（醫院，:378-389）→ config/getJobTypeByOrgIdAjax
//     （:161）＋表 3 欄（:16-26）＋編輯＝雙欄穿梭 picklist modal（:421-512：dialogOrg/dialogJobType
//     disabled＋searchOrganizationId（-1 '--'，:452-456）＋searchDepartment 即時過濾（:41-49）＋
//     4 移動鈕（:473-476→:288-333）＋隱藏 dialogDepartment/dialogDepartment2（計畫管理/行政管理
//     科部，:484-499）；新增/刪除＝legacy dead code 照實不畫（D26-7：showModal(id<0) 無入口/
//     deleteJobTypeDeparment 無引用）。
//
// 誠實標註（KNOWN-NOT-DONE D26-x，詳 src/ui/docs/KNOWN-NOT-DONE.md）：
//   - D26-1：權限模型（isPermitUpdate/isPermitDelete/isSysAdmin/isJobTypeAdmin）不鏡像——
//     種子旗標（deletable/erasable）等價（D20-2/D23-5 先例）。
//   - D26-2：級聯不鏡像（jobType modal 雙部門依醫院級聯 init_dialog :119-140＝選項池平列；
//     data-value 屬性缺失＝預設值鏈未生效＝legacy-known-issue）；organization 查詢 onchange 即查
//     ＝查詢鈕等價。
//   - D26-3：comment roleLimit（selectpicker 多選）＋useType==451 聯動＋非 UPDATE 權限 useType
//     鎖 '450'＋jobTypeId==0 myJobTypeId trick 不鏡像。
//   - D26-4：device/jobTitle modal code 欄以 id 唯讀呈現（模板語意；legacy code 可編輯）。
//   - D26-5：jobType errorMsg 累加式驗證（:243-267）＝mock 單訊息取首項。
//   - D26-6：organization isAdmin 寫死 0（:158）照實。
//   - D26-7：jobTypeDepartment 新增/刪除 dead code 照實不畫。
//   - D26-8：jobType 查詢鈕權限包錯＋data-target 死 id＝照實不鏡像。
//   - 查詢為伺服器端過濾（getJobTypeListAjax/getJobTypeByOrgIdAjax）→ QUERY_PASSTHROUGH
//     種子直出（批次 23 先例），過濾語意【雛形值待實機校準】。
//   - 選項池（醫院/部門/學員類別/分組/年度格式）＝種子平列池【雛形值待實機校準】。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame } from '@/components/layouts';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
} from '@/mocks/resource-rules';
import { fieldErrorsOf } from '../templates/shared';
import {
  deleteWithUsageCheck,
  legacyAlertFallback,
  StatusToggleCell,
  type BehaviorFlowContext,
  type UsageCheckTexts,
} from '../templates/page-behaviors';

/** 醫院選項（查詢區 server render 無空 option＝預選第一筆；jobType.jsp:443-447/:378-380）。 */
const ORG_SELECT_OPTIONS = ACCOUNT_ORGANIZATION_OPTIONS;

/** 學員類別/分組設定/年度格式選項（modal select；legacy 由 server render【雛形值待實機校準】）。 */
const STUDENT_TYPE_OPTIONS = [
  { value: 'student', label: '住院醫師' },
  { value: 'pgy', label: 'PGY' },
];
const IS_GROUP_OPTIONS = [
  { value: '1', label: '是' },
  { value: '0', label: '否' },
];
const ANNUAL_DISP_OPTIONS = [
  { value: '1', label: '西元年' },
  { value: '0', label: '民國年' },
];

// ---------------------------------------------------------------------------
// 訓練類別設定（config-job-type，display 頁型→complexBody）
// ---------------------------------------------------------------------------

const JOB_TYPE_USAGE_CHECK: UsageCheckTexts = {
  deleteUsed: '訓練類別已被使用，不能刪除，請改成停用', // jobType.jsp:388
  disableUsed: '訓練類別已被使用，停用後將不可再使用此訓練類別，是否確定要停用?', // :355
  disableUnused: '停用後將不可再使用此訓練類別，是否確定要停用?', // :361
  enable: '是否確定要啟用?', // :372
};

export function JobTypeBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1；
  // 僅查詢區 select 接線——dialog 為條件渲染、非凍結 diff 面，維持原常數）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  const [organizationId, setOrganizationId] = useState(ORG_SELECT_OPTIONS[0]!.value);
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; row: Row | null } | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  async function doQuery(): Promise<void> {
    setBusy(true);
    try {
      // config/getJobTypeListAjax（jobType.jsp:226，參 organizationId :224；server 端過濾＝直出）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: { organizationId },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  function reload(): void {
    void doQuery();
  }

  const flowCtx: BehaviorFlowContext = {
    pageId,
    reload,
    openEdit: (row) => openEdit(row),
    openCreate: () => openCreate(),
    dataSource,
    dialogs,
  };

  function openCreate(): void {
    // addJobType（jobType.jsp:142-151：{id:0,name:'',sort:0,isEnable:1}）。
    setFieldErrors({});
    setValues({ category: '', score: '0', status: '啟用', organizationId, studentType: '', isGroup: '', annualDisp: '', adminDeptId: '', managerDeptId: '' });
    setEditing({ mode: 'create', row: null });
  }

  function openEdit(row: Row): void {
    // showEdit（jobType.jsp:177-189 帶值；isEnable==0 全鎖＋藏存檔 :193-203）。
    setFieldErrors({});
    setValues({
      category: String(row['category'] ?? ''),
      score: String(row['score'] ?? ''),
      status: String(row['status'] ?? ''),
      organizationId: String(row['organizationId'] ?? ORG_SELECT_OPTIONS[0]!.value),
      studentType: String(row['studentType'] ?? ''),
      isGroup: String(row['isGroup'] ?? ''),
      annualDisp: String(row['annualDisp'] ?? ''),
      adminDeptId: String(row['adminDeptId'] ?? ''),
      managerDeptId: String(row['managerDeptId'] ?? ''),
      updateUserName: String(row['updateUserName'] ?? ''),
      updateDate: String(row['updateDate'] ?? ''),
    });
    setEditing({ mode: 'edit', row });
  }

  async function save(): Promise<void> {
    if (editing === null) return;
    setFieldErrors({});
    try {
      if (editing.mode === 'create') {
        await dataSource.create(pageId, values);
      } else {
        await dataSource.update(pageId, String(editing.row?.id ?? ''), values);
      }
      setEditing(null);
      reload();
      // 成功/失敗文案＝server 回應（changeJobTypeEnableType 生效版 :398-414 同族）【雛形值待實機校準】。
      await dialogs.info(t('state.saveSuccess'));
    } catch (cause: unknown) {
      // errorMsg 累加式（:243-267）＝mock 單訊息取首項（D26-5）。
      const fields = fieldErrorsOf(cause);
      if (Object.keys(fields).length > 0) {
        setFieldErrors(fields);
      } else {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'actions', header: '', align: 'center' },
    { key: 'category', header: '訓練類別名稱', align: 'center' },
    { key: 'score', header: '排序', align: 'center' },
    { key: 'status', header: '狀態', align: 'center' },
  ];

  const locked = editing?.mode === 'edit' && String(editing.row?.['status'] ?? '') === '停用';

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="訓練類別設定" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（jobType.jsp:439-459；單欄醫院）。 */}
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-organization-id`}>
              醫院：
            </label>
            <Select
              uiId={`${pageId}-query-organization-id`}
              name="organizationId"
              value={organizationId}
              onChange={setOrganizationId}
              options={overrideOptions('organizationId') ?? ORG_SELECT_OPTIONS}
              {...(overrideOptions('organizationId') ? {} : { placeholder: '請選擇' })}
            />
          </div>
          <div className="col-span-2 flex gap-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
            {/* 新增（jobType.jsp:437 licenseAdd → addJobType :142-151）。 */}
            <Button uiId={`${pageId}-add-btn`} variant="default" size="sm" type="button" onClick={openCreate}>
              新增
            </Button>
          </div>
        </div>
        {/* 表（欄定義 jobType.jsp:28-76；4 欄含操作欄；刪除條件 :29 erasable==1 && isEnable==1）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(_row, index) => `job-type-row-${String(index)}`}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            const rowKey = `job-type-row-${String(rows.indexOf(row))}`;
            if (columnKey === 'actions') {
              const deletable =
                String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用';
              return deletable ? (
                <Button
                  uiId={`${pageId}-result-row-${rowKey}-delete`}
                  variant="danger"
                  size="sm"
                  type="button"
                  dataEffect={`remove:${pageId}`}
                  onClick={() => {
                    void deleteWithUsageCheck(flowCtx, row, JOB_TYPE_USAGE_CHECK);
                  }}
                >
                  {t('action.delete')}
                </Button>
              ) : null;
            }
            if (columnKey === 'category') {
              // 名稱連結＝.showEdit（jobType.jsp:38-41）。
              return (
                <Button uiId={`${pageId}-result-row-${rowKey}-edit`} variant="link" size="sm" type="button" onClick={() => openEdit(row)}>
                  {String(row['category'] ?? '')}
                </Button>
              );
            }
            if (columnKey === 'status') {
              return (
                <StatusToggleCell
                  row={row}
                  columnKey={columnKey}
                  ctx={flowCtx}
                  texts={JOB_TYPE_USAGE_CHECK}
                  canToggle={(target) => String(target['erasable'] ?? '') === '1'}
                />
              );
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      {/* modal（jobType.jsp:472-577 dialogModal 9 欄＋hidden id；雙部門級聯＝選項池平列 D26-2）。 */}
      {editing !== null ? (
        <ModalFrame
          uiId={`${pageId}-dialog-modal`}
          open
          title={editing.mode === 'create' ? '新增訓練類別名稱' : '修改訓練類別名稱'}
          onClose={() => setEditing(null)}
          footer={
            locked ? undefined : (
              <Button uiId={`${pageId}-dialog-modal-save`} variant="primary" size="sm" type="button" onClick={() => void save()}>
                存檔
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-2 text-sm">
            {DialogField(`${pageId}-dialog-organizationId`, '所屬醫院：', (
              <Select uiId={`${pageId}-dialog-organization-id`} name="organizationId" value={values['organizationId'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, organizationId: next }))} options={ORG_SELECT_OPTIONS} disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-category`, '訓練類別名稱：', (
              <TextInput uiId={`${pageId}-dialog-category`} name="category" value={values['category'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, category: next }))} maxLength={30} disabled={locked} invalid={fieldErrors['category'] !== undefined} />
            ), fieldErrors['category'])}
            {DialogField(`${pageId}-dialog-studentType`, '學員類別：', (
              <Select uiId={`${pageId}-dialog-student-type`} name="studentType" value={values['studentType'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, studentType: next }))} options={STUDENT_TYPE_OPTIONS} placeholder="請選擇" disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-isGroup`, '分組設定：', (
              <Select uiId={`${pageId}-dialog-is-group`} name="isGroup" value={values['isGroup'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, isGroup: next }))} options={IS_GROUP_OPTIONS} placeholder="請選擇" disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-annualDisp`, '年度格式：', (
              <Select uiId={`${pageId}-dialog-annual-disp`} name="annualDisp" value={values['annualDisp'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, annualDisp: next }))} options={ANNUAL_DISP_OPTIONS} placeholder="請選擇" disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-adminDeptId`, '訓練類別管理部門：', (
              <Select uiId={`${pageId}-dialog-admin-dept-id`} name="adminDeptId" value={values['adminDeptId'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, adminDeptId: next }))} options={ACCOUNT_DEPARTMENT_OPTIONS} placeholder="請選擇" disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-managerDeptId`, '訓練類別行政管理單位：', (
              <Select uiId={`${pageId}-dialog-manager-dept-id`} name="managerDeptId" value={values['managerDeptId'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, managerDeptId: next }))} options={ACCOUNT_DEPARTMENT_OPTIONS} placeholder="請選擇" disabled={locked} />
            ))}
            {DialogField(`${pageId}-dialog-score`, '排序：', (
              <TextInput uiId={`${pageId}-dialog-score`} name="score" value={values['score'] ?? ''} onChange={(next) => setValues((current) => ({ ...current, score: next }))} disabled={locked} />
            ))}
            {/* isEnable 欄（jobType.jsp:552-560 註解掉）＝不畫照實。 */}
            {editing.mode === 'edit' ? (
              <div className="text-ink-muted">
                異動人員：{values['updateUserName'] ?? ''}／異動日期：{values['updateDate'] ?? ''}
              </div>
            ) : null}
          </div>
        </ModalFrame>
      ) : null}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 訓練類別部門維護（config-job-type-department，crud-list 頁型→complexBody；picklist）
// ---------------------------------------------------------------------------

export function JobTypeDepartmentBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  const [queryOrgId, setQueryOrgId] = useState(ORG_SELECT_OPTIONS[0]!.value);
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  // picklist 狀態（jobTypeDepartment.jsp:421-512）。
  const [toDepartments, setToDepartments] = useState<readonly string[]>([]);
  // 批次 47 uiId 衛生：穿梭兩池改 ref 直取——原 document.querySelector 用 [data-ui-id=${pageId}-…]
  // 引號包值的模板字串查詢，會被 check-ui-ids.ts 的 STATIC_ID_ATTR 當字面 id 擷取（連 ${pageId}
  // 一起）驗 kebab-case；ref 直取語意等價（同 component 渲染的元素）且免 selector 字串。
  const fromBoxRef = useRef<HTMLSelectElement>(null);
  const toBoxRef = useRef<HTMLSelectElement>(null);
  const [searchOrg, setSearchOrg] = useState('-1');
  const [searchText, setSearchText] = useState('');
  const [departmentsToError, setDepartmentsToError] = useState('');
  const [hiddenDepts, setHiddenDepts] = useState<{ deptId: string; managerDeptId: string }>({ deptId: '', managerDeptId: '' });

  async function doQuery(): Promise<void> {
    setBusy(true);
    try {
      // config/getJobTypeByOrgIdAjax（:161，參 organizationId :157-159；server 端過濾＝直出）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: { queryOrgId },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  // 首載一次（legacy ready 後手動查詢；雛形 ready 即查詢＝批次 14 ready 即查詢先例）。
  // D-3 配套（批次 48）：列 edit 鈕自報 data-modal-trigger 後，extract-modals 在初始穩定態
  // 收集 trigger——初始無列＝trigger 不存在＝穿梭 modal 永遠抽不到（D-3 第 2 點）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 首載一次。
  useEffect(() => {
    void doQuery();
  }, []);

  function openEdit(row: Row): void {
    // showEdit（:30-34；dialogOrg/dialogJobType disabled :146-147；To 池＝row.departmentsTo）。
    const to = String(row['departmentsTo'] ?? '').split(',').filter((item) => item !== '');
    setToDepartments(to);
    setSearchOrg('-1');
    setSearchText('');
    setDepartmentsToError('');
    setHiddenDepts({
      deptId: String(row['adminDepartment'] ?? ''),
      managerDeptId: String(row['managerDepartment'] ?? ''),
    });
    setEditing(row);
  }

  // From 池（getAllDepartmentInOrg :252-280）：searchOrganizationId='-1'（'--'）＝空池，選醫院才填充
  // 部門平列池−已選；searchDepartment 即時過濾（:41-49）。
  const fromDepartments = searchOrg === '-1' ? [] : ACCOUNT_DEPARTMENT_OPTIONS.filter(
    (option) => !toDepartments.includes(option.label),
  ).filter((option) => searchText === '' || option.label.includes(searchText));

    async function save(): Promise<void> {
    if (editing === null) return;
    setDepartmentsToError('');
    const values: Record<string, string> = {
      unit: String(editing['unit'] ?? ''),
      category: String(editing['category'] ?? ''),
      score: String(editing['score'] ?? ''),
      departmentsTo: toDepartments.join(','),
      adminDepartment: hiddenDepts.deptId,
      managerDepartment: hiddenDepts.managerDeptId,
    };
    try {
      await dataSource.update(pageId, String(editing.id ?? ''), values);
      setEditing(null);
      await doQuery();
      // 成功且 response.orgId==queryOrgId 才回填（:216）→ mock 直出等價重查。
      await dialogs.info(t('state.saveSuccess'));
    } catch (cause: unknown) {
      const fields = fieldErrorsOf(cause);
      if (fields['departmentsTo'] !== undefined) {
        setDepartmentsToError(fields['departmentsTo'] ?? '修改失敗，請選擇參與部門');
      } else {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'unit', header: '醫院', align: 'center' },
    { key: 'category', header: '訓練類別', align: 'center' },
    { key: 'score', header: '排序序號', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="訓練類別部門維護" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（jobTypeDepartment.jsp:378-389；單欄醫院）。 */}
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-query-org-id`}>
              醫院：
            </label>
            <Select
              uiId={`${pageId}-query-query-org-id`}
              name="queryOrgId"
              value={queryOrgId}
              onChange={setQueryOrgId}
              options={overrideOptions('queryOrgId') ?? ORG_SELECT_OPTIONS}
              {...(overrideOptions('queryOrgId') ? {} : { placeholder: '請選擇' })}
            />
          </div>
          <div className="col-span-2 flex gap-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        {/* 表（欄定義 :16-26；jobTypeName 連結＝.showEdit :20-22；新增/刪除＝dead code 不畫＝D26-7）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(_row, index) => `jtd-row-${String(index)}`}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            if (columnKey === 'category') {
              return (
                <Button
                  uiId={`${pageId}-result-row-jtd-row-${String(rows.indexOf(row))}-edit`}
                  variant="link"
                  size="sm"
                  type="button"
                  // D-3 自報（批次 48，鏡像 leave 先例 page-behaviors.tsx:3030）：名稱連結＝showEdit
                  // （:20-22）→ 開下方穿梭 modal。extract-modals 只點 [data-modal-trigger]（C3 紅線），
                  // 不自報＝departments-from/to 驗證點永遠不可達。
                  dataModalTrigger={`${pageId}-dialog-modal`}
                  onClick={() => openEdit(row)}
                >
                  {String(row['category'] ?? '')}
                </Button>
              );
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      {/* 編輯 modal（:421-512：雙欄穿梭＋隱藏計畫管理/行政管理科部）。 */}
      {editing !== null ? (
        <ModalFrame
          uiId={`${pageId}-dialog-modal`}
          open
          title="修改訓練類別部門"
          onClose={() => setEditing(null)}
          footer={
            <Button uiId={`${pageId}-dialog-modal-save`} variant="primary" size="sm" type="button" onClick={() => void save()}>
              存檔
            </Button>
          }
        >
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm">醫院：</label>
              <Select
                uiId={`${pageId}-dialog-dialog-org`}
                name="dialogOrg"
                value={String(editing['unit'] ?? '')}
                onChange={() => undefined}
                options={[{ value: String(editing['unit'] ?? ''), label: String(editing['unit'] ?? '') }]}
                disabled
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm">訓練類別：</label>
              <Select
                uiId={`${pageId}-dialog-dialog-job-type`}
                name="dialogJobType"
                value={String(editing['category'] ?? '')}
                onChange={() => undefined}
                options={[{ value: String(editing['category'] ?? ''), label: String(editing['category'] ?? '') }]}
                disabled
              />
            </div>
            {/* searchOrganizationId（:452-456，首 option -1 '--'；change→getAllDepartmentInOrg）。 */}
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm">部門所屬醫院：</label>
              <Select
                uiId={`${pageId}-dialog-search-organization-id`}
                name="searchOrganizationId"
                value={searchOrg}
                onChange={setSearchOrg}
                options={[{ value: '-1', label: '--' }, ...ACCOUNT_ORGANIZATION_OPTIONS]}
              />
            </div>
            {/* searchDepartment 即時過濾（:41-49）。 */}
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm">搜尋部門：</label>
              <TextInput uiId={`${pageId}-dialog-search-department`} name="searchDepartment" value={searchText} onChange={setSearchText} placeholder="搜尋部門" />
            </div>
            {/* 雙欄穿梭（departmentsFrom :467／departmentsTo :481；4 移動鈕 :473-476）。
                批次 47 uiId 衛生：uiId 由 dialog-departmentsFrom/To 改 kebab-case
                dialog-departments-from/to（UI_ID_PATTERN；name 屬性照 legacy 不動）。 */}
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <select
                ref={fromBoxRef}
                data-ui-id={`${pageId}-dialog-departments-from`}
                name="departmentsFrom"
                multiple
                size={8}
                className="h-40 w-full rounded border border-border px-2 py-1 text-sm"
              >
                {fromDepartments.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="flex flex-col gap-1">
                <Button uiId={`${pageId}-dialog-move-all-to-right`} variant="default" size="sm" type="button" onClick={() => setToDepartments((current) => [...new Set([...current, ...fromDepartments.map((option) => option.label)])])}>
                  全部右移
                </Button>
                <Button uiId={`${pageId}-dialog-move-selected-to-right`} variant="default" size="sm" type="button" onClick={() => {
                  const selected = [...(fromBoxRef.current?.selectedOptions ?? [])].map((option) => option.text);
                  setToDepartments((current) => [...new Set([...current, ...selected])]);
                }}>
                  選取右移
                </Button>
                <Button uiId={`${pageId}-dialog-move-selected-to-left`} variant="default" size="sm" type="button" onClick={() => {
                  const selected = [...(toBoxRef.current?.selectedOptions ?? [])].map((option) => option.text);
                  setToDepartments((current) => current.filter((item) => !selected.includes(item)));
                }}>
                  選取左移
                </Button>
                <Button uiId={`${pageId}-dialog-move-all-to-left`} variant="default" size="sm" type="button" onClick={() => setToDepartments([])}>
                  全部左移
                </Button>
              </div>
              <select
                ref={toBoxRef}
                data-ui-id={`${pageId}-dialog-departments-to`}
                name="departmentsTo"
                multiple
                size={8}
                className="h-40 w-full rounded border border-border px-2 py-1 text-sm"
              >
                {toDepartments.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {departmentsToError !== '' ? (
              <div data-ui-id={`${pageId}-dialog-error-departments-to`} className="text-danger">
                {departmentsToError}
              </div>
            ) : null}
            {/* 隱藏區（:484-499 display:none）：dialogDepartment 計畫管理科部/dialogDepartment2 行政管理科部。 */}
            <input type="hidden" name="dialogDepartment" value={hiddenDepts.deptId} data-ui-id={`${pageId}-dialog-dialog-department`} />
            <input type="hidden" name="dialogDepartment2" value={hiddenDepts.managerDeptId} data-ui-id={`${pageId}-dialog-dialog-department2`} />
          </div>
        </ModalFrame>
      ) : null}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 共用小元件
// ---------------------------------------------------------------------------

/** modal 欄（label＋control＋欄位級錯誤；批次 17 VoteMaintenanceBody queryField 同構）。 */
function DialogField(
  uiId: string,
  label: string,
  control: ReactElement,
  error?: string,
): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <label className="w-40 shrink-0 text-right text-sm" htmlFor={uiId}>
          {label}
        </label>
        {control}
      </div>
      {error !== undefined && error !== '' ? (
        <div data-ui-id={`${uiId}-error`} className="text-danger">
          {error}
        </div>
      ) : null}
    </div>
  );
}
